import DOMPurify from 'dompurify'

/**
 * Sanitizes HTML content to prevent XSS attacks with enhanced security
 */
export const sanitizeHtml = (content: string): string => {
  if (!content || typeof content !== 'string') return ''
  
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'],
    ALLOWED_ATTR: [],
    FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  })
}

/**
 * Sanitizes content specifically for display purposes (stricter)
 */
export const sanitizeForDisplay = (content: string): string => {
  if (!content || typeof content !== 'string') return ''
  
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br'],
    ALLOWED_ATTR: []
  })
}

/**
 * Sanitizes all user inputs strictly for security
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return ''
  
  return input
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/data:text\/html/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
}

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

/**
 * Validates phone number format (German format)
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false
  
  const phoneRegex = /^(\+49|0)[1-9]\d{8,11}$/
  return phoneRegex.test(phone.replace(/\s/g, ''))
}

/**
 * Validates password strength with enhanced security requirements
 */
export const validatePassword = (password: string): { valid: boolean; error?: string } => {
  if (!password || typeof password !== 'string') {
    return { valid: false, error: 'Passwort ist erforderlich' }
  }
  
  if (password.length < 8) {
    return { valid: false, error: 'Passwort muss mindestens 8 Zeichen lang sein' }
  }
  
  if (password.length > 128) {
    return { valid: false, error: 'Passwort darf maximal 128 Zeichen lang sein' }
  }
  
  if (!/(?=.*[a-z])/.test(password)) {
    return { valid: false, error: 'Passwort muss mindestens einen Kleinbuchstaben enthalten' }
  }
  
  if (!/(?=.*[A-Z])/.test(password)) {
    return { valid: false, error: 'Passwort muss mindestens einen Großbuchstaben enthalten' }
  }
  
  if (!/(?=.*\d)/.test(password)) {
    return { valid: false, error: 'Passwort muss mindestens eine Zahl enthalten' }
  }
  
  if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(password)) {
    return { valid: false, error: 'Passwort muss mindestens ein Sonderzeichen enthalten' }
  }
  
  // Check for common weak patterns
  const weakPatterns = [
    /(.)\1{3,}/,  // 4+ repeated characters
    /123456/,     // sequential numbers
    /password/i,  // contains "password"
    /admin/i,     // contains "admin"
    /qwerty/i     // keyboard patterns
  ]
  
  for (const pattern of weakPatterns) {
    if (pattern.test(password)) {
      return { valid: false, error: 'Passwort enthält unsichere Muster. Bitte wählen Sie ein stärkeres Passwort.' }
    }
  }
  
  return { valid: true }
}

/**
 * Validates file upload with enhanced security checks
 */
export const validateFile = (file: File, maxSize: number = 5 * 1024 * 1024): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'Keine Datei ausgewählt' }
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'Datei ist zu groß (max. 5MB)' }
  }
  
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Nur JPEG, PNG und WebP Dateien sind erlaubt' }
  }
  
  // Check for suspicious file extensions
  const suspiciousExtensions = ['.php', '.js', '.html', '.htm', '.exe', '.bat', '.cmd']
  const fileName = file.name.toLowerCase()
  if (suspiciousExtensions.some(ext => fileName.includes(ext))) {
    return { valid: false, error: 'Verdächtige Dateiendung erkannt' }
  }
  
  return { valid: true }
}

/**
 * Validates text input length and content
 */
export const validateTextInput = (
  text: string, 
  minLength: number = 1, 
  maxLength: number = 1000,
  fieldName: string = 'Text'
): { valid: boolean; error?: string } => {
  if (!text || typeof text !== 'string') {
    return { valid: false, error: `${fieldName} ist erforderlich` }
  }
  
  const sanitized = sanitizeInput(text)
  
  if (sanitized.length < minLength) {
    return { valid: false, error: `${fieldName} muss mindestens ${minLength} Zeichen lang sein` }
  }
  
  if (sanitized.length > maxLength) {
    return { valid: false, error: `${fieldName} darf maximal ${maxLength} Zeichen lang sein` }
  }
  
  return { valid: true }
}

/**
 * Validates numeric input
 */
export const validateNumericInput = (
  value: number | string,
  min?: number,
  max?: number,
  fieldName: string = 'Wert'
): { valid: boolean; error?: string } => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  
  if (isNaN(numValue)) {
    return { valid: false, error: `${fieldName} muss eine gültige Zahl sein` }
  }
  
  if (min !== undefined && numValue < min) {
    return { valid: false, error: `${fieldName} muss mindestens ${min} sein` }
  }
  
  if (max !== undefined && numValue > max) {
    return { valid: false, error: `${fieldName} darf maximal ${max} sein` }
  }
  
  return { valid: true }
}

/**
 * Enhanced rate limiting helper for authentication attempts
 */
export const createRateLimiter = (windowMs: number = 15 * 60 * 1000, maxAttempts: number = 5) => {
  const attempts = new Map<string, { count: number; resetTime: number }>()
  
  return {
    isRateLimited: (identifier: string): boolean => {
      const now = Date.now()
      const userAttempts = attempts.get(identifier)
      
      if (!userAttempts || now > userAttempts.resetTime) {
        attempts.set(identifier, { count: 1, resetTime: now + windowMs })
        return false
      }
      
      if (userAttempts.count >= maxAttempts) {
        return true
      }
      
      userAttempts.count++
      return false
    },
    
    reset: (identifier: string): void => {
      attempts.delete(identifier)
    },
    
    getRemainingTime: (identifier: string): number => {
      const userAttempts = attempts.get(identifier)
      if (!userAttempts) return 0
      
      const now = Date.now()
      return Math.max(0, userAttempts.resetTime - now)
    },
    
    getAttemptCount: (identifier: string): number => {
      const userAttempts = attempts.get(identifier)
      return userAttempts?.count || 0
    }
  }
}

/**
 * Create authentication rate limiter with secure defaults
 */
export const authRateLimiter = createRateLimiter(15 * 60 * 1000, 5) // 5 attempts per 15 minutes

/**
 * Validates URL to prevent SSRF attacks
 */
export const validateUrl = (url: string): { valid: boolean; error?: string } => {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'URL ist erforderlich' }
  }
  
  try {
    const parsedUrl = new URL(url)
    
    // Only allow HTTPS in production
    if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
      return { valid: false, error: 'Nur HTTP und HTTPS URLs sind erlaubt' }
    }
    
    // Block localhost and private IP ranges in production
    const hostname = parsedUrl.hostname.toLowerCase()
    const blockedHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1']
    
    if (blockedHosts.includes(hostname)) {
      return { valid: false, error: 'Lokale URLs sind nicht erlaubt' }
    }
    
    // Block private IP ranges
    if (hostname.match(/^(10\.|172\.(1[6-9]|2[0-9]|3[01])\.|192\.168\.)/)) {
      return { valid: false, error: 'Private IP-Adressen sind nicht erlaubt' }
    }
    
    return { valid: true }
  } catch (error) {
    return { valid: false, error: 'Ungültiges URL-Format' }
  }
}

/**
 * Enhanced security headers for CSP
 */
export const getSecurityHeaders = () => {
  return {
    'Content-Security-Policy': [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval'",
      "style-src 'self' 'unsafe-inline'",
      "img-src 'self' data: https:",
      "font-src 'self'",
      "connect-src 'self' https://luvqxmadaytntwbwmxob.supabase.co",
      "frame-ancestors 'none'"
    ].join('; '),
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()'
  }
}